"use client";

import { useCallback, useEffect, useState } from "react";
import { z } from "zod";
import { BookOpen, LineChart, RotateCcw, TrendingUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { CalculatorTemplate } from "@/components/calculator/CalculatorTemplate";
import { InputForm } from "@/components/calculator/InputForm";
import { ResultCard } from "@/components/calculator/ResultCard";
import { AreaChartWrapper, PieChartWrapper } from "@/components/calculator/ChartWrapperLazy";
import { ExplanationTable } from "@/components/calculator/ExplanationTable";
import { ShareButtons } from "@/components/calculator/ShareButtons";
import { formatCurrency } from "@/lib/utils/currency";
import { calculateCompoundInterest } from "@/lib/calculators/compound-interest";

const PRINCIPAL_MAX = 999_999_999_999_999; // 999 trillion - prevents overflow

const CONTRIBUTION_MAX = 999_999_999_999; // 999 billion per period

const schema = z.object({
  principal: z.coerce
    .number()
    .min(1, "Enter a positive amount")
    .max(PRINCIPAL_MAX, "Maximum is $999 trillion"),
  rate: z.coerce.number().min(0.01, "Enter a valid rate").max(100),
  years: z.coerce.number().min(1, "Enter at least 1 year").max(150),
  frequency: z.coerce.number().min(1).max(365),
  contribution: z.coerce.number().min(0, "Must be 0 or more").max(CONTRIBUTION_MAX),
  contributionFrequency: z.coerce.number().min(1).max(365),
});

type FormValues = z.infer<typeof schema>;

const FREQUENCY_OPTIONS = [
  { value: 365, label: "Daily" },
  { value: 12, label: "Monthly" },
  { value: 4, label: "Quarterly" },
  { value: 1, label: "Annual" },
] as const;

const CONTRIBUTION_FREQUENCY_OPTIONS = [
  { value: 365, label: "Daily" },
  { value: 52, label: "Weekly" },
  { value: 26, label: "Each paycheck (bi-weekly)" },
  { value: 12, label: "Monthly" },
  { value: 4, label: "Quarterly" },
  { value: 1, label: "Annually" },
] as const;

const defaultValues: FormValues = {
  principal: 10000,
  rate: 6,
  years: 10,
  frequency: 12,
  contribution: 0,
  contributionFrequency: 12,
};

const PRESETS: { label: string; values: Partial<FormValues> }[] = [
  { label: "5% savings", values: { principal: 10000, rate: 5, years: 10, contribution: 200, contributionFrequency: 12, frequency: 12 } },
  { label: "7% investment", values: { principal: 25000, rate: 7, years: 20, contribution: 500, contributionFrequency: 12, frequency: 12 } },
  { label: "10% aggressive", values: { principal: 5000, rate: 10, years: 30, contribution: 300, contributionFrequency: 12, frequency: 12 } },
];

const CHART_COLOR = "#2563eb";
const PIE_COLORS = { principal: "#64748b", interest: "#2563eb", contributions: "#0ea5e9" };

function parseValuesFromSearchParams(
  params: URLSearchParams
): Partial<FormValues> | null {
  const principal = params.get("principal");
  const rate = params.get("rate");
  const years = params.get("years");
  if (!principal || !rate || !years) return null;
  const vals: Partial<FormValues> = {
    principal: parseFloat(principal),
    rate: parseFloat(rate),
    years: parseInt(years, 10),
  };
  const frequency = params.get("frequency");
  if (frequency != null) vals.frequency = parseInt(frequency, 10);
  const contribution = params.get("contribution");
  if (contribution != null) vals.contribution = parseFloat(contribution);
  const contributionFrequency = params.get("contributionFrequency");
  if (contributionFrequency != null)
    vals.contributionFrequency = parseInt(contributionFrequency, 10);
  return vals;
}

function buildShareUrl(values: FormValues): string {
  const params = new URLSearchParams();
  params.set("principal", String(values.principal));
  params.set("rate", String(values.rate));
  params.set("years", String(values.years));
  params.set("frequency", String(values.frequency));
  if (values.contribution > 0)
    params.set("contribution", String(values.contribution));
  if (values.contributionFrequency !== values.frequency)
    params.set("contributionFrequency", String(values.contributionFrequency));
  if (typeof window !== "undefined") {
    return `${window.location.origin}${window.location.pathname}?${params.toString()}`;
  }
  return "";
}

function useMounted() {
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  return mounted;
}

export function CompoundInterestCalculator() {
  const mounted = useMounted();
  const [formValues, setFormValues] = useState<FormValues>(defaultValues);
  const [resetKey, setResetKey] = useState(0);
  const [result, setResult] = useState(() =>
    calculateCompoundInterest({
      ...defaultValues,
    })
  );
  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    const parsed = parseValuesFromSearchParams(params);
    if (parsed) {
      const merged = { ...defaultValues, ...parsed } as FormValues;
      const parsedResult = schema.safeParse(merged);
      if (parsedResult.success) {
        setFormValues(merged);
        setResult(calculateCompoundInterest(merged));
        setResetKey((k) => k + 1);
      }
    }
  }, []);

  const handleReset = useCallback(() => {
    setFormValues(defaultValues);
    setResult(calculateCompoundInterest({ ...defaultValues }));
    setResetKey((k) => k + 1);
  }, []);

  const handlePreset = useCallback((preset: Partial<FormValues>) => {
    const merged = { ...defaultValues, ...formValues, ...preset } as FormValues;
    const parsed = schema.safeParse(merged);
    if (parsed.success) {
      setFormValues(parsed.data);
      setResult(calculateCompoundInterest(parsed.data));
      setResetKey((k) => k + 1);
    }
  }, [formValues]);

  const handleChange = useCallback((values: FormValues) => {
    setFormValues(values);
    setResult(
      calculateCompoundInterest({
        ...values,
      })
    );
  }, []);

  const chartData = result.chartData.map((d) => ({
    label: `Year ${d.year}`,
    balance: Number.isFinite(d.total) ? d.total : 0,
  }));

  return (
    <CalculatorTemplate
      chartSectionLabel="Growth over time"
      chartSectionIcon={LineChart}
      explanationSectionLabel="Formula & examples"
      explanationSectionIcon={BookOpen}
      inputsSection={
        <div className="space-y-4">
          <InputForm
            key={resetKey}
            schema={schema}
            defaultValues={formValues}
            syncFields={{ source: "frequency", target: "contributionFrequency" }}
          fields={[
            {
              name: "principal",
              label: "Principal amount",
              tooltip: "The initial amount you invest or deposit.",
              suffix: " USD",
              min: 1,
              max: PRINCIPAL_MAX,
              step: 1,
              inputMode: "decimal",
            },
            {
              name: "rate",
              label: "Annual interest rate",
              tooltip: "The annual interest rate your money earns.",
              suffix: "%",
              min: 0,
              max: 100,
              step: 0.01,
              inputType: "slider-number",
              inputMode: "decimal",
            },
            {
              name: "years",
              label: "Time period",
              tooltip: "How long your money will grow.",
              suffix: " years",
              min: 1,
              max: 150,
              sliderMax: 50,
              step: 1,
              inputType: "slider-number",
            },
            {
              name: "frequency",
              label: "Compounding frequency",
              tooltip: "How often interest is added to your balance. Monthly is common for savings.",
              inputType: "select",
              options: [...FREQUENCY_OPTIONS],
            },
            {
              name: "contribution",
              label: "Additional contribution",
              tooltip: "Regular amounts you add (e.g. monthly deposits).",
              suffix: " USD",
              min: 0,
              max: CONTRIBUTION_MAX,
              step: 1,
              inputMode: "decimal",
              placeholder: "0",
              hideSpinner: true,
            },
            {
              name: "contributionFrequency",
              label: "Contribution frequency",
              tooltip: "How often you make contributions.",
              inputType: "select",
              options: [...CONTRIBUTION_FREQUENCY_OPTIONS],
            },
          ]}
            onChange={handleChange}
          />
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs text-muted-foreground">Presets:</span>
            {PRESETS.map((p) => {
              const isSelected = Object.entries(p.values).every(
                ([k, v]) => formValues[k as keyof FormValues] === v
              );
              return (
                <Button
                  key={p.label}
                  type="button"
                  variant={isSelected ? "default" : "outline"}
                  size="sm"
                  onClick={() => handlePreset(p.values)}
                  className="text-xs"
                >
                  {p.label}
                </Button>
              );
            })}
          </div>
          <div className="flex gap-2">
            <ShareButtons shareUrl={buildShareUrl(formValues)} />
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={handleReset}
              className="text-muted-foreground"
            >
              <RotateCcw className="size-3.5" />
              Reset
            </Button>
          </div>
        </div>
      }
      resultsSection={
        <div className="space-y-4">
          <ResultCard
            icon={TrendingUp}
            metrics={[
              {
                label: "Total amount",
                value: result.totalDisplay ?? formatCurrency(result.total),
                highlight: true,
                detail: result.totalInWords,
              },
              {
                label: "Interest earned",
                value: result.interestDisplay ?? formatCurrency(result.interest),
                detail: result.interestInWords,
              },
              {
                label: "Principal",
                value: formatCurrency(result.principal),
              },
              ...(result.totalContributions != null && result.totalContributions > 0
                ? [
                    {
                      label: "Total contributions",
                      value: formatCurrency(result.totalContributions),
                    },
                  ]
                : []),
            ]}
          />
          {mounted && result.total > 0 && (() => {
            const contributions = result.totalContributions ?? 0;
            const pieData = [
              { name: "Principal", value: result.principal, color: PIE_COLORS.principal },
              { name: "Interest", value: result.interest, color: PIE_COLORS.interest },
              ...(contributions > 0 ? [{ name: "Contributions", value: contributions, color: PIE_COLORS.contributions }] : []),
            ].filter((d) => d.value > 0);
            return pieData.length >= 2 ? (
              <div className="rounded-lg border border-border bg-card p-5">
                <p className="mb-4 text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  Breakdown
                </p>
                <PieChartWrapper data={pieData} height={220} />
              </div>
            ) : null;
          })()}
          {result.totalInWords && (
            <div className="rounded-lg border border-primary/20 bg-primary/5 px-4 py-3">
              <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Spelled out
              </p>
              <p className="mt-1 text-base font-medium text-foreground">
                {result.totalInWords} dollars
              </p>
              {result.totalBreakdown && (
                <p className="mt-2 text-sm text-muted-foreground">
                  {result.totalBreakdown}
                </p>
              )}
            </div>
          )}
        </div>
      }
      chartSection={
        mounted ? (
          <AreaChartWrapper
            data={chartData}
            dataKeys={[
              { key: "balance", color: CHART_COLOR, name: "Total balance" },
            ]}
          />
        ) : (
          <div className="h-[300px] w-full animate-pulse rounded-lg bg-muted" />
        )
      }
      explanationSection={
        <div className="space-y-6">
          <div>
            <h2 className="mb-3 text-lg font-semibold text-foreground">
              The compound interest formula
            </h2>
            <p className="mb-3 text-muted-foreground">
              Without regular contributions, the formula is:
            </p>
            <p className="mb-3 font-mono text-sm text-foreground">
              A = P(1 + r/n)^(nt)
            </p>
            <p className="mb-3 text-muted-foreground">
              Where <strong className="text-foreground">A</strong> = final
              amount, <strong className="text-foreground">P</strong> = principal,{" "}
              <strong className="text-foreground">r</strong> = annual rate
              (decimal), <strong className="text-foreground">n</strong> =
              compounding frequency per year, and{" "}
              <strong className="text-foreground">t</strong> = years. With
              regular contributions, each contribution compounds from when it&apos;s
              made; contribution frequency can differ from compounding frequency
              (e.g. monthly contributions with daily compounding).
            </p>
          </div>

          <div>
            <h2 className="mb-3 text-lg font-semibold text-foreground">
              Example calculation
            </h2>
            <p className="mb-3 text-muted-foreground">
              $10,000 at 6% annual rate, compounded monthly, for 10 years:
            </p>
            <ExplanationTable
              headers={["Variable", "Value"]}
              rows={[
                ["Principal (P)", "$10,000"],
                ["Rate (r)", "0.06"],
                ["Compounding (n)", "12 (monthly)"],
                ["Years (t)", "10"],
                ["Calculation", "10,000 × (1 + 0.06/12)^(12×10)"],
                ["Final amount (A)", "≈ $18,194"],
              ]}
            />
          </div>

          <div>
            <h2 className="mb-3 text-lg font-semibold text-foreground">
              Limitations
            </h2>
            <ul className="list-disc space-y-1 pl-5 text-muted-foreground">
              <li>Assumes a fixed rate of return; real investments fluctuate.</li>
              <li>Does not account for taxes, fees, or inflation.</li>
              <li>Use for projections and planning, not as a guarantee of future results.</li>
            </ul>
          </div>
        </div>
      }
    />
  );
}
